import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { take } from 'rxjs/operators';
import App from '../../helpers/app';
import { formatarDataHoraBR } from '../../helpers/date.helper';
import { WhatsAppService, MensagemEnviada, MensagensListResponse, WhatsAppInstancia } from '../../shared/services/whatsapp.service';

@Component({
  selector: 'app-whatsapp-mensagens',
  templateUrl: './whatsapp-mensagens.component.html',
  styleUrls: ['./whatsapp-mensagens.component.scss']
})
export class WhatsappMensagensComponent implements OnInit {
  mensagens: MensagemEnviada[] = [];
  instancias: WhatsAppInstancia[] = [];
  total = 0;
  offset = 0;
  limit = 20;
  page = 1;

  filters = {
    id_instancia: null as number | null,
    destinatario: '',
    data_inicio: '',
    data_fim: '',
    status: null as 'pendente' | 'enviada' | 'entregue' | 'falha' | null
  };

  constructor(
    private whatsapp: WhatsAppService,
    private spinner: NgxSpinnerService,
    private app: App
  ) {}

  ngOnInit() {
    this.carregarInstancias();
    this.carregarMensagens();
  }

  async carregarInstancias() {
    try {
      const res = await this.whatsapp.listarInstancias().pipe(take(1)).toPromise() as WhatsAppInstancia[] | { return?: WhatsAppInstancia[]; data?: WhatsAppInstancia[]; status?: number } | undefined;
      if (this.isErrorResponse(res)) {
        this.instancias = [];
        return;
      }
      this.instancias = Array.isArray(res) ? res : (res?.return ?? res?.data ?? []);
    } catch {
      this.instancias = [];
    }
  }

  private isErrorResponse(res: any): boolean {
    return res != null && typeof res === 'object' && 'status' in res && Number((res as { status?: number }).status) >= 400;
  }

  async carregarMensagens() {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const params: any = {
        offset: this.offset,
        limit: this.limit
      };
      if (this.filters.id_instancia != null) params.id_instancia = this.filters.id_instancia;
      if (this.filters.destinatario?.trim()) params.destinatario = this.filters.destinatario.trim();
      if (this.filters.data_inicio?.trim()) params.data_inicio = this.convertToYMD(this.filters.data_inicio);
      if (this.filters.data_fim?.trim()) params.data_fim = this.convertToYMD(this.filters.data_fim);
      if (this.filters.status) params.status = this.filters.status;

      const res = await this.whatsapp.listarMensagens(params).pipe(take(1)).toPromise() as MensagensListResponse | { status?: number };
      if (this.isErrorResponse(res)) {
        this.mensagens = [];
        this.total = 0;
        await this.app.alert('Erro', 'Não foi possível carregar as mensagens. Tente novamente.', 'error');
        return;
      }
      const data = res as MensagensListResponse;
      this.mensagens = data?.data ?? [];
      this.total = data?.total ?? 0;
      this.offset = data?.offset ?? this.offset;
      this.limit = data?.limit ?? this.limit;
    } catch (e) {
      this.mensagens = [];
      this.total = 0;
      await this.app.alert('Erro', 'Não foi possível carregar as mensagens. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }

  convertToYMD(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const d = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const y = parts[2];
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  }

  aplicarFiltros() {
    this.page = 1;
    this.offset = 0;
    this.carregarMensagens();
  }

  limparFiltros() {
    this.filters = {
      id_instancia: null,
      destinatario: '',
      data_inicio: '',
      data_fim: '',
      status: null
    };
    this.page = 1;
    this.offset = 0;
    this.carregarMensagens();
  }

  onPageChange(p: number) {
    this.page = p;
    this.offset = (p - 1) * this.limit;
    this.carregarMensagens();
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.limit) || 1;
  }

  Math = Math;

  get startItem(): number {
    return this.total === 0 ? 0 : (this.page - 1) * this.limit + 1;
  }

  get endItem(): number {
    return Math.min(this.page * this.limit, this.total);
  }

  formatarData(s: string | null): string {
    return formatarDataHoraBR(s);
  }

  tipoLabel(tipo: string): string {
    const map: Record<string, string> = {
      text: 'Texto',
      image: 'Imagem',
      video: 'Vídeo',
      document: 'Documento',
      audio: 'Áudio',
      sticker: 'Figurinha'
    };
    return map[tipo] || tipo || '–';
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      pendente: 'Pendente',
      enviada: 'Enviada',
      entregue: 'Entregue',
      falha: 'Falha'
    };
    return map[status] || status || '–';
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      pendente: 'badge-warning',
      enviada: 'badge-info',
      entregue: 'badge-success',
      falha: 'badge-danger'
    };
    return map[status] || 'badge-secondary';
  }

  async excluirMensagem(msg: MensagemEnviada) {
    if (msg.status !== 'pendente') return;
    const ok = await this.app.confirm('Confirmar', `Remover esta mensagem da fila?`);
    if (!ok) return;
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.excluirMensagem(msg.id).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) {
        await this.app.alert('Erro', 'Não foi possível remover a mensagem. Tente novamente.', 'error');
        return;
      }
      await this.app.alert('Sucesso', 'Mensagem removida da fila.', 'success');
      await this.carregarMensagens();
    } catch (e) {
      await this.app.alert('Erro', 'Não foi possível remover a mensagem. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }
}
